/**
 * Backend tenants API — current-tenant context hook.
 *
 * Wraps GET /v1/tenants/me. Returns the calling user's tenant id, role
 * (re-affirmed against Cosmos), and display name. The session bootstrap
 * path is allowed to fall back to JWT-only values if the tenant doc is
 * missing (never 500) — see the handler note in tenants-me.ts.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import type { PublicTenantBranding } from './branding-public';
import type { RoleLabelOverrides } from '../labels';
import type { CustomRoleView } from './branding';

/**
 * The tier vocabulary `/v1/tenants/me` actually speaks.
 *
 * The handler canonicalises the stored `TenantDoc.tier` through
 * `canonicalTierKey()` (F-FIRM-R2-010) before it goes on the wire, so the
 * response NEVER carries the legacy `starter` / `professional` strings this
 * type used to declare — it was a straight lie about the payload, and it is
 * the same four keys the billing catalogue and /pricing use.
 *
 * (E2E 2026-08-21/22: a b2b firm was reported as `independent` because the
 * backend mapped legacy `starter` without consulting the tenant's `kind`. That
 * is fixed handler-side; this type is corrected in the same pass so a consumer
 * can compare against a real tier key.)
 */
export type TenantTier = 'independent' | 'practice' | 'practice_plus' | 'enterprise';

export interface TenantsMeResponse {
  tenant_id: string;
  role: string;
  user_sub: string;
  display_name: string | null;
  /** Tenant subdomain slug — white-label runtime uses it to resolve branding
   *  for a logged-in user on bare apex (no subdomain). Null if unknown. */
  slug: string | null;
  /** Published branding overlay (display fields only), or null when none is
   *  published. Same public-safe shape as the by-slug endpoint. */
  branding: PublicTenantBranding | null;
  /** The firm's own titles for the four firm-staff roles, or `{}` when it keeps
   *  the canonical Elreign names. Display-only — the authorization model reads
   *  the role KEY and is untouched by this. Optional for back-compat with
   *  responses minted before the field existed; treat absent as `{}`. */
  roleLabels?: RoleLabelOverrides;
  /** The firm's own role NAMES and the canonical permission set each carries,
   *  or `[]` when it runs the four canonical chairs only. Display-only for the
   *  same reason `roleLabels` is: `base` is the token authz reads and it is
   *  always one of the four. Rides this read (rather than the admin branding
   *  GET) because the directory, the role tags and the invite picker all need
   *  it and none of them is an admin surface. Optional for back-compat —
   *  treat absent as `[]`. */
  customRoles?: CustomRoleView[];
  tier: TenantTier | null;
  features: string[];
  /** True only for a self-managed b2c owner (a `kind: 'b2c'` tenant that is NOT
   *  firm-stewarded, owned by the caller). Gates the "Add a trust" b2c
   *  self-onboard action: a firm-managed member (trustee OR beneficiary), whose
   *  trusts live in a firm-stewarded client tenant, gets `false` and never sees
   *  the button. Optional for back-compat with responses minted before the flag
   *  existed — treat an absent value as `false` (fail-closed). */
  self_managed?: boolean;
  /** Whether a firm stewards this workspace, for any reader of it (backend
   *  `tenants-me.ts`). `null` when the backend could not read the workspace,
   *  absent on a backend older than the field: both mean unknown. Copy that
   *  names "your firm" branches on it (`lib/firm-stewardship.ts`). */
  firm_stewarded?: boolean | null;
  correlation_id: string;
}

export interface UseTenantMeOptions {
  /** Gate the query — pass `false` (e.g. no session yet) to skip the call and
   *  avoid a 401 on unauthenticated surfaces. Defaults to enabled. */
  enabled?: boolean;
  /** Resolve on the operator's OWN Supabase session, ignoring any active
   *  impersonation / firm act-as token. The white-label runtime uses this to
   *  read the FIRM's branding while a firm-user is administering a client — the
   *  act-as token would otherwise scope this read to the client's tenant and
   *  drop the firm's colours mid-act-as. Cached under a distinct key so it never
   *  collides with the act-as-scoped read. */
  useOperatorSession?: boolean;
}

export function useTenantMe(options: UseTenantMeOptions = {}) {
  const useOperatorSession = options.useOperatorSession ?? false;
  return useQuery<TenantsMeResponse>({
    queryKey: useOperatorSession ? ['tenants', 'me', 'operator'] : ['tenants', 'me'],
    queryFn: ({ signal }) =>
      api<TenantsMeResponse>('/api/v1/tenants/me', { signal, useOperatorSession }),
    enabled: options.enabled ?? true,
  });
}
